import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {Observable, BehaviorSubject, merge, of, Subscription} from 'rxjs';
import { ReferenceAreaDataService } from "../../services/reference-area-data.service";
import { ReferenceArea } from "../../models/reference-area";
import { ReferenceAreaEditComponent} from "../reference-area-edit/reference-area-edit.component";
import { ReferenceAreaImportComponent } from "../reference-area-import/reference-area-import.component";
import { switchMap, map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { MessageService } from 'primeng/api';
import { TableStateService } from '../../../../shared/services/table-state.service';
import { ExportFilenameService } from '../../../../shared/services/export-filename.service';
import { DataImportExportService } from '../../../../shared/services/data-import-export.service';

@Component({
  selector: 'app-reference-area-list',
  template: require('./reference-area-list.component.html'),
  styles: [require('./reference-area-list.component.sass')]
})
export class ReferenceAreaListComponent implements OnInit {

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 250,
    itemsPerPageOptions: [50, 100, 250, 500],
    globalFilterFields: ['name', 'description', 'abbreviation', 'type', 'stationCode'],
    exportFilename: ExportFilenameService.generate('Reference-Areas')
  }

  columns: any[];

  @ViewChild(ReferenceAreaEditComponent) referenceAreaEdit: ReferenceAreaEditComponent;
  @ViewChild(ReferenceAreaImportComponent) referenceAreaImport: ReferenceAreaImportComponent;

  private readonly refreshReferenceAreaList$ = new BehaviorSubject(undefined);

  referenceAreas$: Observable<ReferenceArea[] | null >;
  projectId: number;
  referenceAreaIds$: Observable<number[]>;
  languageChangeSubscription: Subscription;
  selectedLanguage: string = 'en'

  constructor(
    private referenceDataService: ReferenceAreaDataService,
    @Inject('ProjectService') private projectService: any,
    private confirmationDialogService: ConfirmationDialogService,
    private translate: TranslateService,
    private messageService: MessageService,
    public tableState: TableStateService,
    public dataImportExportService: DataImportExportService
    ) { 
      this.referenceAreas$ = this.refreshReferenceAreaList$.pipe(
        switchMap(() => merge(
          of(null),
          this.referenceDataService.getReferenceAreas(this.projectId))
      ))

    this.languageChangeSubscription = this.translate.onLangChange.subscribe((language: string) => {
      this.refreshReferenceArea();
      this.selectedLanguage = language;
    })
      //this.referenceAreaIds$ = this.referenceAreas$.pipe(map(referenceAreas => referenceAreas ? referenceAreas.map(referenceArea => referenceArea.id) : []));

  }

  ngOnInit(): void {
    this.projectId = this.projectService.getSelectedProjectId();
    this.refreshReferenceArea()
  }

  refreshReferenceArea = () =>{
  this.columns = [{
    header: this.translate.instant('I18N_GENERAL.NAME'),
    field: 'name',
    type: 'text'
  }, {
    header: this.translate.instant('I18N_REFERENCE_AREAS.DESCRIPTION'),
    field: 'description',
    type: 'text'
  }, {
    header: this.translate.instant('I18N_REFERENCE_AREAS.STATION_CODE'),
    field: 'stationCode',
    type: 'text'
  }, {
    header: this.translate.instant('I18N_REFERENCE_AREAS.ABBREVIATION'),
    field: 'abbreviation',
    type: 'text'
  }, {
    header: this.translate.instant('I18N_REFERENCE_AREAS.TYPE'),
    field: 'type',
    type: 'text'
  }, {
    header: this.translate.instant('I18N_REFERENCE_AREAS.AREA'),
    field: 'latLongList',
    type: 'text',
    hidden: true
  }, {
    header: this.translate.instant('I18N_GENERAL.ACTIONS'),
    type: null,
    disableSorting: true,
    disableSearch: true,
    excludeExport: true
  }]
}
  async addReferenceArea() {
    await this.referenceAreaEdit.showModal()
  }
  
  async viewReferenceArea(referenceArea: ReferenceArea) {
    await this.referenceAreaEdit.showModal(referenceArea, true)
  }

  async editReferenceArea(referenceArea: ReferenceArea) {
    await this.referenceAreaEdit.showModal(referenceArea)
  }

  reload = () => {
    this.refreshReferenceAreaList$.next(undefined);
  }

  onSaveReferenceArea = async (referenceArea: ReferenceArea) => {
    try {
      await this.referenceDataService.saveReferenceAreas(this.projectId, [referenceArea]);
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant('I18N_REFERENCE_AREAS.SAVE_REFERENCE_AREA'),
        detail:`${this.translate.instant("I18N_REFERENCE_AREAS.THE_REFERENCE_AREA")} ${referenceArea.id ? this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED") : this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")}`
      });
      this.reload();
    } catch(error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant("I18N_REFERENCE_AREAS.SAVE_REFERENCE_AREA"),
        detail: error && error.message
      });
    }
  }

  onDeleteReferenceArea = async (referenceArea: ReferenceArea)  => {
    if (referenceArea && referenceArea.id) {
      const confirmed = await this.confirmationDialogService.confirm(
        `${this.translate.instant('I18N_REFERENCE_AREAS.DELETE')} ${referenceArea.name}`,
        this.translate.instant('I18N_REFERENCE_AREAS.DELETE_CONFIRMATION'),
        'danger',
        this.translate.instant('I18N_REFERENCE_AREAS.DELETE'),
        this.translate.instant('I18N_GENERAL.CANCEL'),
        "lg");
      if (confirmed) {
        try {
          await this.referenceDataService.deleteReferenceArea(this.projectId, referenceArea.id, referenceArea.typeCode);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA'),
            detail:`${this.translate.instant("I18N_REFERENCE_AREAS.THE_REFERENCE_AREA")} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
          });
          this.reload();
        } catch (error) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant("I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA"),
            detail: error && error.message
          });
        }
      }
    }
  }

  openImportModal = async () => {
    await this.referenceAreaImport.showModal(this.projectId);
  }

}
